import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import axiosInstance from "../services/axiosInterceptor";
import userAvatar from "../assets/img/img1.jpg";
import {
  adminMenu,
  executiveMenu,
  teachingMenu,
  dashboardMenu,
  applicationsMenu,
  pagesMenu,
  uiElementsMenu,
  advisorMenu,
  manageTeachingMenu,
  admissionMenu,
  registrationMenu,
  headOfGeneralAcademicMenu,
  levelHeadMenu,
  guidanceMenu,
  libraryMenu,
  dormitoryMenu,
  dormNightMenu,
  examPeriodMenu,
  prayerMenu,
  disciplineMenu,
  guardMenu,
  bankMenu,
  feeMenu,
  hrMenu,
  hrSelfMenu,
  nurseMenu,
  nurseSettingsMenu,
  adminOfficeMenu,
  adminOfficeSelfMenu,
  inventoryMenu,
  supplyMenu,
  assessmentMenu,
  islamicRegistrationMenu,
  islamicTeachingMenu,
  islamicAssessmentMenu,
  islamicAdvisorMenu,
  islamicManageTeachingMenu,
  islamicTrackingMenu,
  islamicAcademicMenu,
  studentSearchMenu,
  generalMenu,
  issueReportingMenu,
  issueReportingAdminMenu,
} from "../data/Menu";

import { getFullName, getPosName, getUser, logout } from "../services/authorize";
import { useAuth } from "../services/authProvider";
import {
  MENU_MODE_ALL,
  readGroupState,
  useMenuMode,
  writeGroupState,
} from "../utils/sidebarMenu";

const UPLOAD_BASE = (process.env.REACT_APP_API || "http://localhost:3002/api")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");

const resolveUploadUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${UPLOAD_BASE}${path.startsWith("/") ? path : `/${path}`}`;
};

export default function Sidebar () {
  const scrollBarRef = React.useRef(null);
  const avatarSrc = resolveUploadUrl(getUser()?.t_photo) || userAvatar;

  const updateScroll = React.useCallback(() => {
    if (scrollBarRef.current) scrollBarRef.current.updateScroll();
  }, []);

  const toggleFooterMenu = (e) => {
    e.preventDefault();
    let parent = e.target.closest(".sidebar");
    parent.classList.toggle("footer-menu-show");
  };

  // Close the footer menu when navigating (no preventDefault so the Link still routes)
  const closeFooterMenu = (e) => {
    const parent = e.currentTarget.closest(".sidebar");
    if (parent) parent.classList.remove("footer-menu-show");
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <Link to="/" className="sidebar-logo">
          SIS WTI
        </Link>
      </div>
      <PerfectScrollbar
        className="sidebar-body"
        ref={scrollBarRef}
      >
        <SidebarMenu onUpdateSize={updateScroll} />
      </PerfectScrollbar>
      <div className="sidebar-footer">
        <div className="sidebar-footer-top">
          <div className="sidebar-footer-thumb">
            <img src={avatarSrc} alt="" />
          </div>
          <div className="sidebar-footer-body">
            <h6>
              <Link to="/account/profile">{getFullName()}</Link>
            </h6>
            <p>{getPosName()}</p>
          </div>
          <Link
            onClick={toggleFooterMenu}
            to=""
            className="dropdown-link"
          >
            <i className="ri-arrow-down-s-line"></i>
          </Link>
        </div>
        <div className="sidebar-footer-menu">
          <nav className="nav">
            <Link to="/account/profile/edit" onClick={closeFooterMenu}>
              <i className="ri-edit-2-line"></i> แก้ไขข้อมูลส่วนตัว
            </Link>
            <Link to="/account/profile" onClick={closeFooterMenu}>
              <i className="ri-profile-line"></i> ดูโปรไฟล์
            </Link>
          </nav>
          <hr />
          <nav className="nav">
            <Link to="/general/help" onClick={closeFooterMenu}>
              <i className="ri-question-line"></i> ศูนย์ช่วยเหลือ
            </Link>
            <Link to="/pages/change-password" onClick={closeFooterMenu}>
              <i className="ri-lock-line"></i> เปลี่ยนรหัสผ่าน
            </Link>
            <Link to="/account/settings" onClick={closeFooterMenu}>
              <i className="ri-user-settings-line"></i> การตั้งค่าบัญชี
            </Link>
            <Link to="" onClick={() => logout(() => window.location.reload())}>
              <i className="ri-logout-box-r-line"></i> ออกจากระบบ
            </Link>
          </nav>
        </div>
      </div>
    </div>
  );
}

// ครูหอพักไม่มีตำแหน่งของตัวเอง — ถามจาก server ว่าถูกมอบหมายเขตไหม (หรือเห็นทุกเขต)
// ครั้งเดียวตอนแถบข้างขึ้น ได้ไม่ครบก็แค่ไม่โชว์เมนู หน้าเช็คชื่อยังตรวจสิทธิ์เองที่ server
// คืน true เฉพาะครูที่ถูกผูกเขต แต่ไม่ได้เห็นทุกเขตอยู่แล้ว (27/99 เห็นในกลุ่มฝ่ายปกครอง)
function useDormTeacherAccess() {
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    let alive = true;
    axiosInstance.get(`${process.env.REACT_APP_API}/dormitory/night-check/mine`)
      .then(({ data }) => { if (alive) setAllowed(!data.all && data.villages > 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return allowed;
}

// ช่วงสอบ — วันนี้มีรายการปฏิทินประเภท "สอบสามัญ" / "สอบศาสนา" ครอบอยู่ไหม ถามครั้งเดียว
// ตอนแถบข้างขึ้น คืนชื่อกลุ่มเมนูตามภาคที่กำลังสอบ หรือ null = ไม่ขึ้นกลุ่มเมนู
// (หน้าเช็คยังล็อกเองที่ server ไม่ได้พึ่งการซ่อนเมนู)
const EXAM_TRACK_LABEL = { general: "สามัญ", islamic: "ศาสนา" };
function useExamPeriod() {
  const [period, setPeriod] = useState({ label: null, tracks: [] });
  useEffect(() => {
    let alive = true;
    axiosInstance.get(`${process.env.REACT_APP_API}/exam-absence/active`)
      .then(({ data }) => {
        if (!alive) return;
        const tracks = ["general", "islamic"].filter((t) => (data.events || []).some((e) => e.track === t));
        setPeriod({
          label: tracks.length ? `ช่วงสอบ${tracks.map((t) => EXAM_TRACK_LABEL[t]).join(" / ")}` : null,
          tracks,
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return period;
}

function useIsRtl() {
  const [isRtl, setIsRtl] = useState(
    () => document.documentElement.getAttribute('dir') === 'rtl'
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsRtl(document.documentElement.getAttribute('dir') === 'rtl');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] });
    return () => observer.disconnect();
  }, []);
  return isRtl;
}

const isActiveLink = (link, pathname) =>
  !!link && (pathname === link || pathname.startsWith(`${link}/`));

const menusHaveActiveLink = (menus, pathname) =>
  menus.some((menu) =>
    menu.some(
      (m) =>
        isActiveLink(m.link, pathname) ||
        (m.submenu || []).some((sm) => isActiveLink(sm.link, pathname))
    )
  );

// Helper to render menu
function populateMenu(m, useAr, onUpdateSize) {
  // Helper to toggle submenu while closing siblings' submenu
  const toggleSubMenu = (e) => {
    e.preventDefault();
    let parent = e.target.closest(".nav-item");
    let node = parent.parentNode.firstChild;
    while (node) {
      if (node !== parent && node.nodeType === Node.ELEMENT_NODE)
        node.classList.remove("show");
      node = node.nextElementSibling || node.nextSibling;
    }
    parent.classList.toggle("show");
    if (onUpdateSize) onUpdateSize();
  };

  const menu = m.map((m, key) => {
    const displayLabel = (useAr && m.labelJawi) ? m.labelJawi : m.label;
    let sm;
    if (m.submenu) {
      sm = m.submenu.map((sm, key) => {
        const subLabel = (useAr && sm.labelJawi) ? sm.labelJawi : sm.label;
        return (
          <NavLink to={sm.link} className="nav-sub-link" key={key}>
            {subLabel}
          </NavLink>
        );
      });
    }

    return (
      <li key={key} className="nav-item">
        {!sm ? (
          <NavLink to={m.link} className="nav-link">
            <i className={m.icon}></i> <span>{displayLabel}</span>
          </NavLink>
        ) : (
          <div onClick={toggleSubMenu} className="nav-link has-sub">
            <i className={m.icon}></i> <span>{displayLabel}</span>
          </div>
        )}
        {m.submenu && <nav className="nav nav-sub">{sm}</nav>}
      </li>
    );
  });

  return <ul className="nav nav-sidebar">{menu}</ul>;
}

function NavGroup({ id, label, menus, useAr, onUpdateSize, defaultOpen, highlight }) {
  const { pathname } = useLocation();
  const menuMode = useMenuMode();
  const hasActiveLink = menusHaveActiveLink(menus, pathname);

  // หมวดของหน้าที่กำลังเปิดอยู่กางเสมอ ไม่งั้นผู้ใช้ไม่เห็นว่าตัวเองอยู่ตรงไหน
  // นอกนั้นถือตามที่ผู้ใช้เคยกดไว้ ถ้ายังไม่เคยกดค่อยดูโหมดกับ defaultOpen
  const resolveOpen = () => {
    if (hasActiveLink) return true;
    const stored = readGroupState()[id];
    if (typeof stored === "boolean") return stored;
    return menuMode === MENU_MODE_ALL || !!defaultOpen;
  };

  const [open, setOpen] = useState(resolveOpen);

  useEffect(() => {
    setOpen(resolveOpen());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuMode, hasActiveLink]);

  // ความสูงในกล่องเปลี่ยน ต้องบอก PerfectScrollbar ให้คำนวณแถบเลื่อนใหม่
  useEffect(() => {
    if (onUpdateSize) onUpdateSize();
  }, [open, onUpdateSize]);

  const toggle = () => {
    setOpen((prev) => {
      writeGroupState(id, !prev);
      return !prev;
    });
  };

  return (
    <div className={`nav-group${open ? " show" : ""}${highlight ? " nav-group-highlight" : ""}`}>
      <div className="nav-label" onClick={toggle}>
        {label}
      </div>
      {menus.map((menu, key) => (
        <React.Fragment key={key}>
          {populateMenu(menu, useAr, onUpdateSize)}
        </React.Fragment>
      ))}
    </div>
  );
}

function SidebarMenu(props) {
  const isRtl = useIsRtl();
  const onUpdateSize = props.onUpdateSize;

  const { posIds } = useAuth()
  const userPositions = posIds && posIds.length > 0 ? posIds : [1]

  const group = (id, label, menus, useAr = false, defaultOpen = false, highlight = false) => (
    <NavGroup
      id={id}
      label={label}
      menus={menus}
      useAr={useAr}
      onUpdateSize={onUpdateSize}
      defaultOpen={defaultOpen}
      highlight={highlight}
    />
  );

  // ช่วงสอบ — ขึ้นเฉพาะวันสอบ ไฮไลต์และกางไว้ให้ครูคุมสอบหาเจอทันที
  const { label: examPeriodLabel, tracks: examTracks } = useExamPeriod();
  const visibleExamPeriodMenu = examPeriodMenu.filter(
    (item) => (!item.positions || item.positions.some((pos) => userPositions.includes(pos)))
      && (!item.track || examTracks.includes(item.track))
  );

  // เมนูละหมาดกรองรายรายการ ไม่ใช่ทั้งกลุ่ม เพราะสิทธิ์ของแต่ละหน้าไม่เท่ากัน
  // (ดูหมายเหตุใน data/Menu.js) กลุ่มจะไม่ขึ้นเลยถ้าไม่เหลือรายการไหนที่กดได้
  const isDormTeacher = useDormTeacherAccess();

  // ฝ่ายปกครองกรองรายรายการ — หัวหน้า (27) เห็นทุกหน้า สารวัตร (16) เห็นเฉพาะงานของสารวัตร
  const visibleDisciplineMenu = disciplineMenu.filter(
    (item) => !item.positions || item.positions.some((pos) => userPositions.includes(pos))
  );

  const visiblePrayerMenu = prayerMenu.filter(
    (item) => !item.positions || item.positions.some((pos) => userPositions.includes(pos))
  );

  // เมนูทะเบียนกรองรายรายการด้วยเหตุผลเดียวกัน — งานระบบ PSIS (29) ใช้กลุ่มนี้ร่วมกับ
  // ฝ่ายทะเบียน แต่เห็นเฉพาะหน้าที่เกี่ยวกับตัวนักเรียน
  const visibleRegistrationMenu = registrationMenu.filter(
    (item) => !item.positions || item.positions.some((pos) => userPositions.includes(pos))
  );

  return (
    <React.Fragment>
      {examPeriodLabel &&
        group("exam-period", examPeriodLabel, [visibleExamPeriodMenu], false, true, true)}

      {/* ข้อมูลนักเรียน — ทุกคนที่ล็อกอินเข้าถึงได้ */}

      {group("student-search", "ข้อมูลนักเรียน", [studentSearchMenu], false, true)}

      {(userPositions.includes(90) || userPositions.includes(99)) &&
        group("executive", "ผู้บริหาร", [executiveMenu])}

      {userPositions.includes(1) &&
        group("teaching", "ข้อมูลการสอน", [teachingMenu])}

      {userPositions.includes(2) &&
        group("advisor", "ข้อมูลประจำชั้น", [advisorMenu])}

      {(userPositions.includes(5) || userPositions.includes(99)) &&
        group("manage-teaching", "ข้อมูลการเรียนการสอน", [manageTeachingMenu])}

      {/* ข้อมูลบุคลากรของฉัน — ทุกคนเข้าถึงได้ (ไม่จำกัดตำแหน่ง) */}
      {group("hr-self", "ข้อมูลบุคลากรของฉัน", [hrSelfMenu, adminOfficeSelfMenu])}

      {/* Head of general academic menu for positions 3, 4, and 99*/}
      {(userPositions.includes(3) || userPositions.includes(4) || userPositions.includes(99)) &&
        group("academic-head", "หัวหน้าวิชาการสามัญ", [headOfGeneralAcademicMenu])}

      {/* หัวหน้าระดับชั้น — ม.ต้น (21) / ม.ปลาย (22); ข้อมูลกรองตามระดับที่ backend.
          admin (99) เข้าถึง 5 หน้านี้ได้อยู่แล้วผ่านเมนู "หัวหน้าวิชาการสามัญ" จึงไม่แสดงเมนูซ้ำ */}
      {(userPositions.includes(21) || userPositions.includes(22)) &&
        group(
          "level-head",
          userPositions.includes(21) ? "หัวหน้าระดับ ม.ต้น" : "หัวหน้าระดับ ม.ปลาย",
          [levelHeadMenu]
        )}

      {/* งานแนะแนว (pos 7) — ติดตามการเข้าคาบแนะแนวรายห้อง (อ่านอย่างเดียว) */}
      {(userPositions.includes(7) || userPositions.includes(99)) &&
        group("guidance", "งานแนะแนว", [guidanceMenu])}

      {/* Admission menu for positions 8, 9, and 99 — ผู้บริหาร (90) เห็นเฉพาะหน้า
          ภาพรวมผ่านเมนู "ตัวเลขและสถิติ" ไม่ต้องเห็นหน้ากรอกข้อมูล */}
      {(userPositions.includes(8) || userPositions.includes(9) || userPositions.includes(99)) &&
        group("admission", "งานรับสมัคร", [admissionMenu])}

      {/* Registration menu for positions 8, 9, 99 และงานระบบ PSIS (29) ที่เห็นเฉพาะ 3 หน้า */}
      {visibleRegistrationMenu.length > 0 &&
        group("registration", "ทะเบียนและหลักสูตร", [visibleRegistrationMenu])}

      {/* Assessment menu for positions 10, 11, and 99 */}
      {(userPositions.includes(10) || userPositions.includes(11) || userPositions.includes(99)) &&
        group("assessment", "ฝ่ายวัดผลและประเมินผล", [assessmentMenu])}

      {/* Library menu for positions 13 and 99 */}
      {(userPositions.includes(13) || userPositions.includes(99)) &&
        group("library", "ห้องสมุด", [libraryMenu])}

      {/* Dormitory menu for positions 99 and 17 */}
      {(userPositions.includes(99) || userPositions.includes(17)) &&
        group("dormitory", "หอพัก", [dormitoryMenu])}

      {/* ครูหอพัก — ครูทั่วไปใต้ฝ่ายปกครองที่ถูกมอบหมายเขต (ไม่มีตำแหน่งของตัวเอง) */}
      {isDormTeacher && group("dorm-teacher", "ครูหอพัก", [dormNightMenu])}

      {/* เช็คชื่อละหมาด — ครูเวรหอ (17) ฝ่ายปกครอง (16) และครูศาสนา (50, 51)
          ที่คุมแถวซุฮ์รี/อัสรีซึ่งอยู่ในเวลาเรียน */}
      {visiblePrayerMenu.length > 0 &&
        group("prayer", "ละหมาด", [visiblePrayerMenu])}

      {visibleDisciplineMenu.length > 0 &&
        group("discipline", "ฝ่ายปกครอง", [visibleDisciplineMenu])}

      {/* ห้องพยาบาล — พยาบาล (28) และเจ้าหน้าที่หอพัก (17) ที่ต้องบันทึกตอนกลางคืน */}
      {(userPositions.includes(28) || userPositions.includes(17) || userPositions.includes(99)) &&
        group("nurse", "ห้องพยาบาล",
          (userPositions.includes(28) || userPositions.includes(99))
            ? [nurseMenu, nurseSettingsMenu]
            : [nurseMenu])}

      {/* จุดตรวจประตู — รปภ./ยาม (24) และ admin (99); ฝ่ายปกครองเห็นเมนูนี้ในกลุ่มฝ่ายปกครองอยู่แล้ว */}
      {(userPositions.includes(24) || userPositions.includes(99)) &&
        group("guard", "งานรักษาความปลอดภัย", [guardMenu])}

      {(userPositions.includes(18) || userPositions.includes(99)) &&
        group("bank", "ธนาคารนักเรียน", [bankMenu])}

      {/* Fee / finance menu for positions 25 and 99 */}
      {(userPositions.includes(25) || userPositions.includes(99)) &&
        group("fee", "การเงิน / ค่าใช้จ่ายนักเรียน", [feeMenu])}

      {/* HR menu for positions 20 (งานบุคลากร), 26 (หัวหน้าบุคลากร) and 99 */}
      {(userPositions.includes(20) || userPositions.includes(26) || userPositions.includes(99)) &&
        group("hr", "งานบุคลากร", [hrMenu])}

      {/* Admin Office menu for positions 23 and 99 */}
      {(userPositions.includes(23) || userPositions.includes(99)) &&
        group("admin-office", "งานธุรการ", [adminOfficeMenu])}

      {/* งานพัสดุ/วัสดุ — พัสดุ (19), ธุรการ (23, งานพัสดุเป็นส่วนหนึ่งของธุรการ) และ admin (99) */}
      {(userPositions.includes(19) || userPositions.includes(23) || userPositions.includes(99)) &&
        group("inventory", "งานพัสดุครุภัณฑ์", [inventoryMenu])}

      {(userPositions.includes(19) || userPositions.includes(23) || userPositions.includes(99)) &&
        group("supply", "วัสดุและของใช้", [supplyMenu])}

      {/* Islamic Teaching menu (pos 50, 99) */}
      {(userPositions.includes(50) || userPositions.includes(99)) &&
        group(
          "islamic-teaching",
          isRtl ? 'تدريس القسم الديني' : 'การสอนภาคศาสนา',
          [islamicTeachingMenu],
          isRtl
        )}

      {/* Islamic Advisor menu (pos 51, 99) */}
      {(userPositions.includes(51) || userPositions.includes(99)) &&
        group(
          "islamic-advisor",
          isRtl ? 'وكيل صف القسم الديني' : 'ครูประจำชั้นห้องเรียนศาสนา',
          [islamicAdvisorMenu],
          isRtl
        )}

      {/* Islamic Assessment menu (pos 52, 99) */}
      {(userPositions.includes(52) || userPositions.includes(99)) &&
        group(
          "islamic-assessment",
          isRtl ? 'تقييم القسم الديني' : 'วัดผลภาคศาสนา',
          [islamicAssessmentMenu],
          isRtl
        )}

      {/* Islamic Registration menu (pos 52, 99) */}
      {(userPositions.includes(52) || userPositions.includes(99)) &&
        group(
          "islamic-registration",
          isRtl ? 'بهاݢين ڤندافتارن اݢام' : 'ทะเบียนภาคศาสนา',
          [islamicRegistrationMenu],
          isRtl
        )}

      {/* Islamic student tracking menu — ครูประจำชั้นศาสนา (51), หัวหน้าระดับ (54/55/56 ตอนต้น/กลาง/ปลาย,
          58 AIP, 59 STP), หัวหน้าวิชาการศาสนา (57) — ขอบเขตข้อมูลกรองที่ backend */}
      {[51, 54, 55, 56, 57, 58, 59, 99].some(p => userPositions.includes(p)) &&
        group(
          "islamic-tracking",
          isRtl ? 'متابعة الطلاب' : 'ติดตามนักเรียนภาคศาสนา',
          [islamicTrackingMenu],
          isRtl
        )}

      {/* Islamic academic head menu — หัวหน้าวิชาการศาสนา (57) และ admin (99)
          รายงาน/ติดตามทั้งภาคศาสนา อ่านอย่างเดียว (ดู server/routes/islamicAcademicRoute.js) */}
      {(userPositions.includes(57) || userPositions.includes(99)) &&
        group(
          "islamic-academic",
          isRtl ? 'اکاديميک اڬام' : 'หัวหน้าวิชาการภาคศาสนา',
          [islamicAcademicMenu],
          isRtl
        )}

      {/* Islamic Manage Teaching menu (pos 53, 99) */}
      {(userPositions.includes(53) || userPositions.includes(99)) &&
        group(
          "islamic-manage-teaching",
          isRtl ? 'ڬلوله ڤڠاجارن اڬام' : 'จัดการการสอนภาคศาสนา',
          [islamicManageTeachingMenu],
          isRtl
        )}

      {/* Admin menu (pos 99 only) */}
      {userPositions.includes(99) &&
        group("admin", "ผู้ดูแลระบบ", [adminMenu])}

      {/* เมนูทั่วไป — ทุกคนเข้าถึงได้ (ไม่จำกัดตำแหน่ง) */}
      {group("general", "ทั่วไป", [generalMenu])}

      {/* แจ้งปัญหา / แจ้งซ่อม — ทุกคนเข้าถึงได้ */}
      {group("issue-reporting", "แจ้งปัญหา / แจ้งซ่อม", [issueReportingMenu])}

      {/* จัดการระบบแจ้งปัญหา — admin (pos 99) เท่านั้น */}
      {userPositions.includes(99) &&
        group("issue-reporting-admin", "จัดการระบบแจ้งปัญหา", [issueReportingAdminMenu])}
    </React.Fragment>
  );
}

window.addEventListener("click", function (e) {
  // Close sidebar footer menu when clicked outside of it
  let tar = e.target;
  let sidebar = document.querySelector(".sidebar");
  if (!tar.closest(".sidebar-footer") && sidebar) {
    sidebar.classList.remove("footer-menu-show");
  }

  // Hide sidebar offset when clicked outside of sidebar
  if (!tar.closest(".sidebar") && !tar.closest(".menu-link")) {
    document.querySelector("body").classList.remove("sidebar-show");
  }
});

window.addEventListener("load", function () {
  let skinMode = localStorage.getItem("sidebar-skin");
  let HTMLTag = document.querySelector("html");

  if (skinMode) {
    HTMLTag.setAttribute("data-sidebar", skinMode);
  }
});
